/**
 * Service de gestion des notifications push
 *
 * Pour activer les vraies notifications push:
 * 1. Generer des cles VAPID: npx web-push generate-vapid-keys
 * 2. Ajouter VITE_VAPID_PUBLIC_KEY dans .env.local
 * 3. Configurer le backend pour envoyer des push avec la cle privee
 */

// Cle VAPID publique pour les notifications push (a configurer en production)
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

// Debug function (désactivé en production)
const log = (..._args: unknown[]) => {
  // Logs désactivés pour un code propre
};

export type NotificationPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

export interface NotificationPreferences {
  criticalStock: boolean;
  dlcAlerts: boolean;
  weeklyReport: boolean;
  pushEnabled: boolean;
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  criticalStock: true,
  dlcAlerts: true,
  weeklyReport: false,
  pushEnabled: false,
};

/**
 * Verifier si les notifications sont supportees
 */
export function isNotificationSupported(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator;
}

/**
 * Obtenir l'etat actuel de la permission
 */
export function getNotificationPermission(): NotificationPermissionState {
  if (!isNotificationSupported()) {
    return 'unsupported';
  }
  return Notification.permission;
}

/**
 * Demander la permission pour les notifications
 */
export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (!isNotificationSupported()) {
    return 'unsupported';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    log('Erreur demande permission notification:', error);
    return 'denied';
  }
}

/**
 * Souscrire aux notifications push
 * @param forceNew - Si true, supprime l'ancienne subscription et en crée une nouvelle
 */
export async function subscribeToPush(forceNew = false): Promise<PushSubscription | null> {
  if (!isNotificationSupported() || !VAPID_PUBLIC_KEY) {
    console.error('[Push] Non supporté ou clé VAPID manquante. VAPID_PUBLIC_KEY:', VAPID_PUBLIC_KEY ? 'présente' : 'ABSENTE');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    // Vérifier si déjà souscrit
    const existingSubscription = await registration.pushManager.getSubscription();

    if (existingSubscription) {
      if (forceNew) {
        // Supprimer l'ancienne subscription pour en créer une nouvelle
        await existingSubscription.unsubscribe();
      } else {
        return existingSubscription;
      }
    }

    // Créer une nouvelle souscription
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    });

    return subscription;
  } catch (error) {
    console.error('[Push] Erreur souscription push:', error);
    return null;
  }
}

/**
 * Se desabonner des notifications push
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      return true;
    }

    return false;
  } catch (error) {
    log('Erreur desabonnement push:', error);
    return false;
  }
}

/**
 * Envoyer une notification locale (pour les tests et les alertes locales)
 */
export async function sendLocalNotification(
  title: string,
  options?: NotificationOptions
): Promise<boolean> {
  if (!isNotificationSupported() || getNotificationPermission() !== 'granted') {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(title, {
      icon: '/icons/icon-192.png',
      badge: '/icons/icon-192.png',
      tag: 'stockchef',
      ...options,
    });
    return true;
  } catch (error) {
    log('Erreur envoi notification:', error);
    return false;
  }
}

/**
 * Obtenir les preferences de notification stockees localement
 */
export function getNotificationPreferences(): NotificationPreferences {
  try {
    const stored = localStorage.getItem('notification_preferences');
    if (stored) {
      return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
    }
  } catch (error) {
    log('Erreur lecture preferences:', error);
  }
  return DEFAULT_PREFERENCES;
}

/**
 * Sauvegarder les preferences de notification
 */
export function saveNotificationPreferences(prefs: Partial<NotificationPreferences>): NotificationPreferences {
  const current = getNotificationPreferences();
  const updated = { ...current, ...prefs };

  try {
    localStorage.setItem('notification_preferences', JSON.stringify(updated));
  } catch (error) {
    log('Erreur sauvegarde preferences:', error);
  }

  return updated;
}

/**
 * Verifier les stocks critiques et envoyer des notifications si necessaire
 */
export async function checkAndNotifyCriticalStock(
  products: Array<{ nom: string; quantite: number; seuil_critique: number }>
): Promise<void> {
  const prefs = getNotificationPreferences();

  if (!prefs.criticalStock || !prefs.pushEnabled || getNotificationPermission() !== 'granted') {
    return;
  }

  const criticalProducts = products.filter(p => p.quantite <= p.seuil_critique);

  if (criticalProducts.length > 0) {
    const firstProduct = criticalProducts[0];
    const body = criticalProducts.length === 1 && firstProduct
      ? `${firstProduct.nom} est en rupture de stock`
      : `${criticalProducts.length} produits sont en rupture de stock`;

    await sendLocalNotification('Alerte Stock Critique', {
      body,
      tag: 'critical-stock',
      requireInteraction: true,
      data: {
        type: 'critical_stock',
        products: criticalProducts.map(p => p.nom),
      },
    });
  }
}

/**
 * Verifier les DLC et envoyer des notifications pour les produits expirant bientot
 */
export async function checkAndNotifyDLC(
  lots: Array<{ produit_nom: string; dlc: string; quantite: number }>
): Promise<void> {
  const prefs = getNotificationPreferences();

  if (!prefs.dlcAlerts || !prefs.pushEnabled || getNotificationPermission() !== 'granted') {
    return;
  }

  const today = new Date();
  const threeDaysLater = new Date(today);
  threeDaysLater.setDate(today.getDate() + 3);

  const expiringLots = lots.filter(lot => {
    const dlcDate = new Date(lot.dlc);
    return dlcDate >= today && dlcDate <= threeDaysLater && lot.quantite > 0;
  });

  if (expiringLots.length > 0) {
    const firstLot = expiringLots[0];
    const body = expiringLots.length === 1 && firstLot
      ? `${firstLot.produit_nom} expire bientot`
      : `${expiringLots.length} produits expirent dans les 3 prochains jours`;

    await sendLocalNotification('Alerte DLC', {
      body,
      tag: 'dlc-alert',
      requireInteraction: true,
      data: {
        type: 'dlc_alert',
        lots: expiringLots.map(l => l.produit_nom),
      },
    });
  }
}

/**
 * Utilitaire: Convertir cle VAPID base64 en Uint8Array
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Hook d'initialisation des notifications au demarrage de l'app
 */
export function initializeNotifications(): void {
  if (!isNotificationSupported()) {
    log('Notifications non supportees sur ce navigateur');
    return;
  }

  // Ecouter les messages du service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
      // Gerer le clic sur une notification
      const { action, data } = event.data;
      log('Notification cliquee:', action, data);

      // Navigation basee sur le type de notification
      if (data?.type === 'critical_stock') {
        window.location.href = '/alerts';
      } else if (data?.type === 'dlc_alert') {
        window.location.href = '/lots';
      }
    }
  });
}
